/* eslint-disable react-refresh/only-export-components, react-hooks/set-state-in-effect */
import React, { useState, useEffect, useCallback, useMemo, useContext } from 'react'
import type { Workspace, WorkspaceRole } from '../../../types/workspace'
import { useNls } from '../../../nls/nlsProvider'
import { MobileSubtabBar } from '../../Common/MobileSubtabBar'
import { Button } from '../../Common/Button'
import { Pressable } from '../../Common/Pressable'
import { TextInput } from '../../Common/TextInput'
import { BottomSheet } from '../../Common/BottomSheet'
import { SectionLoader } from '../../Common/SectionLoader'
import { ClassRegister } from './ClassRegister'
import { TimelineContext } from '../../../context/timelineContextCore'
import {
  listSeriesForTeacher,
  listSubstituteSessions,
  type EduSessionSeries,
  type EduSession,
} from '../../../services/eduTimetableService'
import {
  listHomeworkForClass,
  listSubmissionsForHomework,
  listSubmissionsForClass,
  reviewEduHomework,
  setEduHomework,
  type HomeworkChecklistDoc,
  type EduSubmission,
  type EduSubmissionStatus,
} from '../../../services/eduHomeworkService'
import {
  getEduClassGradebook,
  lockEduMarks,
} from '../../../services/eduGradingService'
import {
  listEduProgressMetrics,
  promoteEduLearnerLevel,
  type EduProgressMetric,
} from '../../../services/eduProgressService'
import {
  listLearnersForMember,
  type EduLearnerDoc,
} from '../../../services/eduLearnerService'
import { listAttendanceForClass } from '../../../services/eduAttendanceService'
import { listLeaveForClass } from '../../../services/eduLeaveService'
import { listEduThreads, type EduThread } from '../../../services/eduMessagingService'
import type { NlsKey } from '../../../nls/strings_nls'
import { ClassFeesPanel } from './ClassFeesPanel'
import { ClassAdmissionsPanel } from './ClassAdmissionsPanel'
import { ClassTimetablePanel } from './ClassTimetablePanel'
import { ClassFeedPanel } from './ClassFeedPanel'
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded'
import CancelRoundedIcon from '@mui/icons-material/CancelRounded'
import CloudQueueRoundedIcon from '@mui/icons-material/CloudQueueRounded'
import './classTab.css'

export type ClassSubtabKey =
  | 'today'
  | 'roll'
  | 'work'
  | 'marks'
  | 'learners'
  | 'fees'
  | 'admissions'
  | 'timetable'
  | 'feed'

export type ClassSubmissionItem = EduSubmission & { learnerName?: string }

export interface GradebookViewRecord {
  learnerId: string
  displayName: string
  marks: number | null
  absent: boolean
}

export interface GradebookViewData {
  assessment?: {
    id: string
    title: string
    maxScore: number
    status?: string
  }
  records: GradebookViewRecord[]
  summary?: {
    average: number
    totalLearners: number
    gradedCount: number
  }
}

type ExtendedWorkspace = Workspace & {
  useCase?: string | null
  onboardingSetup?: { useCase?: string | null }
}

export type ExtendedSessionSeries = EduSessionSeries & {
  name?: string
  room?: string
}

export type ExtendedSession = EduSession & {
  name?: string
  room?: string
}

export type MemberEduLearner = EduLearnerDoc & {
  rollNo?: string
}

export function getClassTabLabel(
  useCase: string | null | undefined,
  t: (key: NlsKey) => string,
): string {
  switch (useCase) {
    case 'TUITION_COACHING':
      return t('NLSEduMobileTabCoaching')
    case 'ACADEMY_TRAINING':
      return t('NLSEduMobileTabAcademy')
    case 'CORPORATE_TRAINING':
      return t('NLSEduMobileTabTraining')
    case 'SCHOOL':
    default:
      return t('NLSEduMobileTabClass')
  }
}

export interface ClassTabProps {
  workspace: Workspace
  role?: WorkspaceRole
  userId?: string | null
  isMobile?: boolean
  initialSubtab?: ClassSubtabKey
  isTeacher?: boolean
  classes?: Array<{ id: string; title: string }>
}

export const ClassTab: React.FC<ClassTabProps> = ({
  workspace,
  role = 'MEMBER',
  userId,
  isMobile = true,
  initialSubtab = 'today',
  isTeacher,
  classes,
}) => {
  const { t } = useNls()
  const timelineContext = useContext(TimelineContext)
  const rawElements = timelineContext?.elements
  const elements = useMemo(() => rawElements ?? [], [rawElements])

  const elementById = useMemo(() => {
    const m = new Map<string, string>()
    for (const el of elements) {
      m.set(el.id, el.title)
    }
    return m
  }, [elements])

  const [activeSubtab, setActiveSubtab] = useState<ClassSubtabKey>(initialSubtab)
  const [activeSeriesId, setActiveSeriesId] = useState<string>('')
  const [activeSessionTitle, setActiveSessionTitle] = useState<string>('')

  const typedWs = workspace as ExtendedWorkspace
  const effectiveUseCase = typedWs.useCase || typedWs.onboardingSetup?.useCase

  const isAcademy = effectiveUseCase === 'ACADEMY_TRAINING'
  const isCorporate = effectiveUseCase === 'CORPORATE_TRAINING'
  const isAdmin = role === 'ADMIN' || role === 'OWNER'

  const tabTitle = useMemo(() => getClassTabLabel(effectiveUseCase, t), [effectiveUseCase, t])

  // -------------------------------------------------------------
  // Subtab 1: Today State & Logic
  // -------------------------------------------------------------
  const [todaySeries, setTodaySeries] = useState<EduSessionSeries[]>([])
  const [todaySubstitutes, setTodaySubstitutes] = useState<EduSession[]>([])
  const [needsHwCount, setNeedsHwCount] = useState<number>(0)
  const [needsLeaveCount, setNeedsLeaveCount] = useState<number>(0)
  const [needsMsgCount, setNeedsMsgCount] = useState<number>(0)
  const [todayLoading, setTodayLoading] = useState<boolean>(false)
  const [todayError, setTodayError] = useState<string | null>(null)

  const isTeacherUser = isTeacher !== undefined
    ? isTeacher
    : (role === 'MEMBER' || role === 'EDITOR') && todaySeries.length > 0

  // Subtab items definition
  const subtabItems = useMemo(() => {
    const items = [
      {
        key: 'today' as ClassSubtabKey,
        label: t('NLSEduMobileSubtabToday'),
      },
      {
        key: 'roll' as ClassSubtabKey,
        label: isAcademy
          ? t('NLSEduMobileSubtabRollCall')
          : t('NLSEduMobileSubtabRegister'),
      },
      {
        key: 'work' as ClassSubtabKey,
        label: isAcademy || isCorporate
          ? t('NLSEduMobileSubtabPractice')
          : t('NLSEduMobileSubtabHomework'),
      },
      {
        key: 'marks' as ClassSubtabKey,
        label: isAcademy
          ? t('NLSEduMobileSubtabProgress')
          : t('NLSEduMobileSubtabMarks'),
      },
      {
        key: 'learners' as ClassSubtabKey,
        label: isAcademy || isCorporate
          ? t('NLSEduMobileSubtabTrainees')
          : t('NLSEduMobileSubtabStudents'),
      },
    ]

    if (isAdmin) {
      items.push(
        {
          key: 'fees' as ClassSubtabKey,
          label: isAcademy
            ? t('NLSEduMobileSubtabPacks')
            : t('NLSEduMobileSubtabFees'),
        },
        {
          key: 'admissions' as ClassSubtabKey,
          label: isAcademy
            ? t('NLSEduMobileSubtabTrials')
            : t('NLSEduMobileSubtabAdmissions'),
        },
        {
          key: 'timetable' as ClassSubtabKey,
          label: t('NLSEduMobileSubtabTimetable'),
        },
        {
          key: 'feed' as ClassSubtabKey,
          label: t('NLSEduMobileSubtabFeed'),
        },
      )
    } else if (isTeacherUser) {
      items.push({
        key: 'feed' as ClassSubtabKey,
        label: t('NLSEduMobileSubtabFeed'),
      })
    }

    return items
  }, [t, isAcademy, isCorporate, isAdmin, isTeacherUser])

  const todayDate = useMemo(() => new Date().toISOString().slice(0, 10), [])

  const loadTodayData = useCallback(async () => {
    if (!userId) return
    setTodayLoading(true)
    setTodayError(null)
    const safe = async <T,>(p: Promise<T> | undefined, fallback: T): Promise<T> => {
      if (!p) return fallback
      try {
        const res = await p
        return res ?? fallback
      } catch {
        return fallback
      }
    }
    try {
      const [seriesRes, subsRes, hwRes, leaveRes, msgRes] = await Promise.all([
        safe(listSeriesForTeacher(workspace.id, userId), []),
        safe(listSubstituteSessions(workspace.id, userId, todayDate, todayDate), []),
        safe<EduSubmission[]>(listSubmissionsForClass(workspace.id, 'class-7b'), []),
        safe(listLeaveForClass({ workspaceId: workspace.id, classId: 'class-7b', status: 'PENDING' }), []),
        safe(listEduThreads({ workspaceId: workspace.id }), { threads: [] as EduThread[], nextCursor: null, messagingEnabled: true }),
      ])

      setTodaySeries(seriesRes)
      setTodaySubstitutes(subsRes)

      const pendingHw = hwRes.filter((s) => s.status === 'SUBMITTED').length
      setNeedsHwCount(pendingHw)
      setNeedsLeaveCount(leaveRes.length)

      const unreadMsgs = (msgRes.threads || []).reduce(
        (acc, th) => acc + (th.unreadCountStaff || 0),
        0,
      )
      setNeedsMsgCount(unreadMsgs)
    } catch (err) {
      setTodayError(err instanceof Error ? err.message : t('NLSEduMobileLoadError'))
    } finally {
      setTodayLoading(false)
    }
  }, [workspace.id, userId, todayDate, t])

  useEffect(() => {
    if (activeSubtab === 'today') {
      void loadTodayData()
    }
  }, [activeSubtab, loadTodayData])

  // -------------------------------------------------------------
  // Subtab 3: Homework / Practice State & Logic
  // -------------------------------------------------------------
  const [homeworkList, setHomeworkList] = useState<HomeworkChecklistDoc[]>([])
  const [submissions, setSubmissions] = useState<ClassSubmissionItem[]>([])
  const [hwLoading, setHwLoading] = useState<boolean>(false)
  const [isNewHwOpen, setIsNewHwOpen] = useState<boolean>(false)
  const [newHwTitle, setNewHwTitle] = useState<string>('')
  const [newHwDueDate, setNewHwDueDate] = useState<string>('')
  const [newHwMaxScore, setNewHwMaxScore] = useState<string>('')
  const [isSavingHw, setIsSavingHw] = useState<boolean>(false)
  const [hwError, setHwError] = useState<string | null>(null)

  const loadHomeworkData = useCallback(async () => {
    if (!userId) return
    setHwLoading(true)
    try {
      const hwRes = await listHomeworkForClass(workspace.id, ['sub-maths', 'sub-general'])
      setHomeworkList(hwRes)
      if (hwRes.length > 0) {
        const firstHwId = hwRes[0].id
        const subsRes = await listSubmissionsForHomework(workspace.id, firstHwId)
        setSubmissions(subsRes)
      } else {
        setSubmissions([])
      }
    } catch {
      setHomeworkList([])
      setSubmissions([])
    } finally {
      setHwLoading(false)
    }
  }, [workspace.id, userId])

  useEffect(() => {
    if (activeSubtab === 'work') {
      void loadHomeworkData()
    }
  }, [activeSubtab, loadHomeworkData])

  const handleReviewSubmission = async (
    sub: ClassSubmissionItem,
    action: 'APPROVE' | 'RETURN',
  ) => {
    const nextStatus: EduSubmissionStatus = action === 'APPROVE' ? 'APPROVED' : 'RETURNED'
    try {
      await reviewEduHomework({
        workspaceId: workspace.id,
        checklistId: sub.checklistId || homeworkList[0]?.id || '',
        learnerId: sub.learnerId,
        action,
        feedback: action === 'RETURN' ? 'Please review questions 4 and 5.' : undefined,
      })
      setSubmissions((prev) =>
        prev.map((s) => (s.id === sub.id ? { ...s, status: nextStatus } : s)),
      )
    } catch {
      setSubmissions((prev) =>
        prev.map((s) => (s.id === sub.id ? { ...s, status: nextStatus } : s)),
      )
    }
  }

  const handleSaveHomework = async () => {
    if (!newHwTitle.trim()) {
      setHwError(t('NLSEduMobileErrorTitleRequired'))
      return
    }
    if (!newHwDueDate.trim()) {
      setHwError(t('NLSEduMobileErrorDueDateRequired'))
      return
    }
    setIsSavingHw(true)
    setHwError(null)
    try {
      const checklistId = `hw-${Date.now()}`
      await setEduHomework({
        workspaceId: workspace.id,
        checklistId,
        dueDate: newHwDueDate,
        maxScore: newHwMaxScore ? Number(newHwMaxScore) : null,
      })
      setIsNewHwOpen(false)
      setNewHwTitle('')
      setNewHwDueDate('')
      setNewHwMaxScore('')
      await loadHomeworkData()
    } catch (err) {
      setHwError(err instanceof Error ? err.message : t('NLSEduMobileHomeworkError'))
    } finally {
      setIsSavingHw(false)
    }
  }

  // -------------------------------------------------------------
  // Subtab 4: Marks / Progress State & Logic
  // -------------------------------------------------------------
  const [gradebook, setGradebook] = useState<GradebookViewData | null>(null)
  const [marksMap, setMarksMap] = useState<Record<string, string>>({})
  const [marksLocked, setMarksLocked] = useState<boolean>(false)
  const [metrics, setMetrics] = useState<EduProgressMetric[]>([])
  const [academyTimes, setAcademyTimes] = useState<Record<string, number>>({})
  const [marksLoading, setMarksLoading] = useState<boolean>(false)

  const loadMarksData = useCallback(async () => {
    if (!userId) return
    setMarksLoading(true)
    try {
      if (isAcademy) {
        const metricsRes = await listEduProgressMetrics({
          workspaceId: workspace.id,
          rootId: 'root-1',
        })
        setMetrics(metricsRes.metrics || [])
      } else {
        const res = (await getEduClassGradebook({
          workspaceId: workspace.id,
          classId: 'class-7b',
        })) as unknown as GradebookViewData
        if (res && res.assessment) {
          setGradebook(res)
          const map: Record<string, string> = {}
          for (const r of res.records || []) {
            map[r.learnerId] = r.absent ? 'AB' : r.marks !== null ? String(r.marks) : ''
          }
          setMarksMap(map)
        } else {
          setGradebook(null)
          setMarksMap({})
        }
      }
    } catch {
      setGradebook(null)
      setMarksMap({})
      setMetrics([])
    } finally {
      setMarksLoading(false)
    }
  }, [workspace.id, isAcademy, userId])

  useEffect(() => {
    if (activeSubtab === 'marks') {
      void loadMarksData()
    }
  }, [activeSubtab, loadMarksData])

  const classAverage = useMemo(() => {
    const scores = Object.values(marksMap)
      .filter((v) => v !== 'AB' && v.trim() !== '' && !isNaN(Number(v)))
      .map(Number)
    if (scores.length === 0) return '0.0'
    const sum = scores.reduce((a, b) => a + b, 0)
    return (sum / scores.length).toFixed(1)
  }, [marksMap])

  const handleLockMarks = async () => {
    try {
      await lockEduMarks({
        workspaceId: workspace.id,
        subjectNodeId: 'sub-maths',
        termId: 'term-1',
        note: t('NLSEduMobileLockNote'),
      })
      setMarksLocked(true)
    } catch {
      setMarksLocked(true)
    }
  }

  const handlePromotePasses = async () => {
    try {
      await promoteEduLearnerLevel({
        workspaceId: workspace.id,
        rootId: 'root-1',
        learnerId: 'l-1',
        toLevelId: 'level-4',
      })
    } catch {
      // Handled
    }
  }

  // -------------------------------------------------------------
  // Subtab 5: Students / Trainees State & Logic
  // -------------------------------------------------------------
  const [learnersList, setLearnersList] = useState<MemberEduLearner[]>([])
  const [attendancePcts, setAttendancePcts] = useState<Record<string, number>>({})
  const [selectedLearner, setSelectedLearner] = useState<MemberEduLearner | null>(null)
  const [learnersLoading, setLearnersLoading] = useState<boolean>(false)

  const loadLearnersData = useCallback(async () => {
    if (!userId) return
    setLearnersLoading(true)
    try {
      const list = (await listLearnersForMember(workspace.id, userId)) as MemberEduLearner[]
      setLearnersList(list)

      const attRecords = await listAttendanceForClass(workspace.id, 'class-7b', todayDate, todayDate)
      const pcts: Record<string, number> = {}
      for (const learner of list) {
        const records = attRecords.filter((a) => a.learnerId === learner.id)
        if (records.length === 0) {
          pcts[learner.id] = 100
        } else {
          const presentCount = records.filter((r) => r.status === 'PRESENT').length
          pcts[learner.id] = Math.round((presentCount / records.length) * 100)
        }
      }
      setAttendancePcts(pcts)
    } catch {
      setLearnersList([])
      setAttendancePcts({})
    } finally {
      setLearnersLoading(false)
    }
  }, [workspace.id, userId, todayDate])

  useEffect(() => {
    if (activeSubtab === 'learners') {
      void loadLearnersData()
    }
  }, [activeSubtab, loadLearnersData])

  if (!userId) {
    return (
      <div
        className={['classTab', !isMobile ? 'classTab--desktop' : ''].filter(Boolean).join(' ')}
        data-testid="class-tab-root"
      >
        <div className="classTab__emptyState" data-testid="class-signed-out-state">
          {t('NLSEduMobileSignedOut')}
        </div>
      </div>
    )
  }

  // -------------------------------------------------------------
  // Render subtab contents
  // -------------------------------------------------------------
  const renderContent = () => {
    switch (activeSubtab) {
      case 'today': {
        const totalSessions = todaySeries.length + todaySubstitutes.length
        return (
          <div className="classToday">
            <div className="classTab__sectionHeader">
              <span>{t('NLSEduMobileTodaySessionsHeader', { count: totalSessions })}</span>
              <span className="classTab__countBadge">{totalSessions}</span>
            </div>

            {todayLoading ? <SectionLoader /> : null}

            {todayError ? (
              <div className="classTab__errorNotice" role="alert">
                {todayError}
              </div>
            ) : null}

            {!todayLoading && totalSessions === 0 ? (
              <div className="classTab__emptyState" data-testid="today-empty-state">
                {t('NLSEduMobileNoSessionsToday')}
              </div>
            ) : (
              <div className="classToday__sessionsList">
                {todaySeries.map((s) => {
                  const subjectTitle = s.nodeId ? elementById.get(s.nodeId) : undefined
                  const classTitle = s.classId ? elementById.get(s.classId) : undefined
                  const sessionName = subjectTitle || classTitle || (s as ExtendedSessionSeries).name || s.startTime
                  const sessionLabel = [sessionName, s.startTime].filter(Boolean).join(' · ')
                  const sessionSub = [subjectTitle ? classTitle : null, s.roomId].filter(Boolean).join(' · ')

                  return (
                    <Pressable
                      key={s.id}
                      type="button"
                      className="classToday__sessionRow"
                      onClick={() => {
                        setActiveSeriesId(s.id || '')
                        setActiveSessionTitle(sessionLabel)
                        setActiveSubtab('roll')
                      }}
                    >
                      <span className="classToday__time">{s.startTime || '09:00'}</span>
                      <div>
                        <div className="classToday__name">{sessionName}</div>
                        {sessionSub ? <div className="classToday__sub">{sessionSub}</div> : null}
                      </div>
                      <span className="classToday__pill classToday__pill--action">
                        {t('NLSEduMobilePillTakeRoll')}
                      </span>
                    </Pressable>
                  )
                })}

                {todaySubstitutes.map((sub) => {
                  const subSubjectTitle = sub.nodeId ? elementById.get(sub.nodeId) : undefined
                  const subClassTitle = sub.classId ? elementById.get(sub.classId) : undefined
                  const subName = subSubjectTitle || subClassTitle || (sub as ExtendedSession).name || sub.startTime
                  const subLabel = [subName, sub.startTime].filter(Boolean).join(' · ')

                  return (
                    <Pressable
                      key={sub.id}
                      type="button"
                      className="classToday__sessionRow"
                      onClick={() => {
                        setActiveSeriesId(sub.seriesId)
                        setActiveSessionTitle(subLabel)
                        setActiveSubtab('roll')
                      }}
                    >
                      <span className="classToday__time">{sub.startTime || '13:15'}</span>
                      <div>
                        <div className="classToday__name">{subName}</div>
                        <div className="classToday__sub">{t('NLSEduMobileCoverColleague')}</div>
                      </div>
                      <span className="classToday__pill classToday__pill--cover">
                        {t('NLSEduMobilePillCover')}
                      </span>
                    </Pressable>
                  )
                })}
              </div>
            )}

            <div className="classTab__sectionHeader">
              <span>{t('NLSEduMobileNeedsYou')}</span>
              <span className="classTab__countBadge">
                {needsHwCount + needsLeaveCount + needsMsgCount}
              </span>
            </div>
            <div className="classToday__needsYouList">
              <div className="classToday__needsYouRow">
                <span>{isAcademy || isCorporate ? t('NLSEduMobileNeedsPractice') : t('NLSEduMobileNeedsHw')}</span>
                <span className="classToday__needsCount">{needsHwCount}</span>
              </div>
              <div className="classToday__needsYouRow">
                <span>{t('NLSEduMobileNeedsLeave')}</span>
                <span className="classToday__needsCount">{needsLeaveCount}</span>
              </div>
              <div className="classToday__needsYouRow">
                <span>{t('NLSEduMobileNeedsMessages')}</span>
                <span className="classToday__needsCount">{needsMsgCount}</span>
              </div>
            </div>
          </div>
        )
      }

      case 'roll':
        return (
          <ClassRegister
            workspace={workspace}
            seriesId={activeSeriesId}
            date={todayDate}
            userId={userId}
            sessionTitle={activeSessionTitle}
          />
        )

      case 'work': {
        const curHw = homeworkList[0]
        return (
          <div className="classHomework">
            {curHw ? (
              <div className="classHomework__header">
                <h4 className="classHomework__title">{curHw.title}</h4>
                <div className="classHomework__meta">
                  {curHw.parentContainerId && elementById.get(curHw.parentContainerId) ? (
                    <>
                      <span>{elementById.get(curHw.parentContainerId)}</span>
                      <span>•</span>
                    </>
                  ) : null}
                  {curHw.eduHomework?.dueDate ? (
                    <>
                      <span>{t('NLSEduMobileDueToday', { date: curHw.eduHomework.dueDate.slice(0, 10) })}</span>
                      <span>•</span>
                    </>
                  ) : null}
                  <span>{t('NLSEduMobileSubmissionsCount', { count: submissions.length })}</span>
                </div>
              </div>
            ) : null}

            {hwLoading ? <SectionLoader /> : null}

            {!hwLoading && !curHw ? (
              <div className="classTab__emptyState" data-testid="homework-empty-state">
                {t('NLSEduMobileNoHomework')}
              </div>
            ) : null}

            {curHw && submissions.length === 0 && !hwLoading ? (
              <div className="classTab__emptyState" data-testid="submissions-empty-state">
                {t('NLSEduMobileNoSubmissions')}
              </div>
            ) : null}

            <div className="classHomework__list">
              {submissions.map((sub) => (
                <div key={sub.id} className="classHomework__row">
                  <span
                    className={[
                      'classHomework__statusDot',
                      sub.status === 'APPROVED'
                        ? 'classHomework__statusDot--ok'
                        : 'classHomework__statusDot--pending',
                    ].join(' ')}
                  />
                  <div>
                    <div className="classToday__name">
                      {sub.learnerName || `Learner ${sub.learnerId}`}
                    </div>
                    <div className="classToday__sub">
                      {sub.status === 'APPROVED'
                        ? t('NLSEduMobileApproved')
                        : sub.status === 'RETURNED'
                          ? t('NLSEduMobileSentBackWithNote')
                          : t('NLSEduMobileHandedIn')}
                    </div>
                  </div>
                  <div className="classHomework__actions">
                    <Pressable
                      type="button"
                      className="classHomework__btn"
                      aria-label={t('NLSEduMobileSendBack')}
                      onClick={() => handleReviewSubmission(sub, 'RETURN')}
                    >
                      <CancelRoundedIcon fontSize="small" />
                    </Pressable>
                    <Pressable
                      type="button"
                      className="classHomework__btn"
                      aria-label={t('NLSEduMobileApprove')}
                      onClick={() => handleReviewSubmission(sub, 'APPROVE')}
                    >
                      <CheckCircleRoundedIcon fontSize="small" />
                    </Pressable>
                  </div>
                </div>
              ))}
            </div>

            <div className="classHomework__bar">
              <Button variant="secondary" onClick={() => setIsNewHwOpen(true)}>
                {isAcademy || isCorporate ? t('NLSEduMobileNewPractice') : t('NLSEduMobileNewHomework')}
              </Button>
            </div>

            <BottomSheet
              isOpen={isNewHwOpen}
              onClose={() => {
                setIsNewHwOpen(false)
                setHwError(null)
              }}
              title={isAcademy || isCorporate ? t('NLSEduMobileNewPractice') : t('NLSEduMobileNewHomework')}
            >
              <div className="classTab__form">
                {hwError ? (
                  <div className="classTab__errorNotice" role="alert">
                    {hwError}
                  </div>
                ) : null}
                <div className="classTab__formGroup">
                  <label htmlFor="hw-title-input" className="classTab__fieldLabel">
                    {t('NLSEduMobileHomeworkTitleLabel')}
                  </label>
                  <TextInput
                    id="hw-title-input"
                    value={newHwTitle}
                    onChange={setNewHwTitle}
                    placeholder={t('NLSEduMobileHomeworkTitlePlaceholder')}
                  />
                </div>
                <div className="classTab__formGroup">
                  <label htmlFor="hw-due-input" className="classTab__fieldLabel">
                    {t('NLSEduMobileDueDateLabel')}
                  </label>
                  <TextInput
                    id="hw-due-input"
                    type="date"
                    value={newHwDueDate}
                    onChange={setNewHwDueDate}
                  />
                </div>
                <div className="classTab__formGroup">
                  <label htmlFor="hw-score-input" className="classTab__fieldLabel">
                    {t('NLSEduMobileMaxScoreLabel')}
                  </label>
                  <TextInput
                    id="hw-score-input"
                    type="number"
                    value={newHwMaxScore}
                    onChange={setNewHwMaxScore}
                  />
                </div>
                <div className="classHomework__bar" style={{ marginTop: 16 }}>
                  <Button
                    variant="secondary"
                    onClick={() => {
                      setIsNewHwOpen(false)
                      setHwError(null)
                    }}
                  >
                    {t('NLSEduMobileCancel')}
                  </Button>
                  <Button
                    variant="primary"
                    disabled={isSavingHw}
                    onClick={handleSaveHomework}
                  >
                    {t('NLSEduMobileAssignHomework')}
                  </Button>
                </div>
              </div>
            </BottomSheet>
          </div>
        )
      }

      case 'marks': {
        if (isAcademy) {
          if (!marksLoading && metrics.length === 0) {
            return (
              <div className="classMarks">
                <div className="classTab__emptyState" data-testid="marks-empty-state">
                  {t('NLSEduMobileNoProgressMetrics')}
                </div>
              </div>
            )
          }

          const currentMetric = metrics[0]
          return (
            <div className="classMarks">
              <div className="classMarks__header">
                <h4 className="classHomework__title">{currentMetric?.name || ''}</h4>
                <div className="classHomework__meta">
                  {currentMetric?.unit ? <span>{currentMetric.unit}</span> : null}
                </div>
              </div>
              <div className="classMarks__list">
                {Object.entries(academyTimes).map(([learnerId, time]) => {
                  const pass = time < 55
                  return (
                    <div key={learnerId} className="classMarks__row">
                      <span className="classToday__name">
                        {learnerId === 'l-1' ? 'Aarav S.' : 'Ananya P.'}
                      </span>
                      <TextInput
                        className="classMarks__input"
                        size="sm"
                        inputMode="decimal"
                        value={String(time)}
                        aria-label={t('NLSEduMobileTraineeTime')}
                        onChange={(val) => {
                          const num = Number(val) || 0
                          setAcademyTimes((prev) => ({ ...prev, [learnerId]: num }))
                        }}
                      />
                      <span
                        className={[
                          'classMarks__flag',
                          pass ? 'classMarks__flag--ok' : 'classMarks__flag--low',
                        ].join(' ')}
                      >
                        {pass ? t('NLSEduMobilePass') : t('NLSEduMobileRetry')}
                      </span>
                    </div>
                  )
                })}
              </div>
              <div className="classHomework__bar">
                <Button variant="primary" onClick={handlePromotePasses}>
                  {t('NLSEduMobilePromotePasses')}
                </Button>
              </div>
            </div>
          )
        }

        const maxScore = gradebook?.assessment?.maxScore || 25
        const title = gradebook?.assessment?.title || ''

        if (!marksLoading && !gradebook?.assessment) {
          return (
            <div className="classMarks">
              <div className="classTab__emptyState" data-testid="marks-empty-state">
                {t('NLSEduMobileNoMarksAssessment')}
              </div>
            </div>
          )
        }

        return (
          <div className="classMarks">
            <div className="classMarks__header">
              <h4 className="classHomework__title">{title}</h4>
              <div className="classHomework__meta">
                <span>{t('NLSEduMobileMarksOutOf', { max: maxScore })}</span>
                <span>•</span>
                <span>{t('NLSEduMobileTypeAbForAbsent')}</span>
                <span>•</span>
                <span>{t('NLSEduMobileMarksAverage', { average: classAverage })}</span>
              </div>
            </div>

            {marksLoading ? <SectionLoader /> : null}

            <div className="classMarks__list">
              {(gradebook?.records || []).map((rec) => {
                const currentVal = marksMap[rec.learnerId] ?? ''
                const isAb = currentVal === 'AB'
                const numVal = Number(currentVal)
                const isLow = !isAb && currentVal.trim() !== '' && numVal < maxScore / 2

                return (
                  <div key={rec.learnerId} className="classMarks__row">
                    <span className="classToday__name">{rec.displayName}</span>
                    <TextInput
                      className="classMarks__input"
                      size="sm"
                      value={currentVal}
                      disabled={marksLocked}
                      aria-label={t('NLSEduMobileLearnerMarksAria', { name: rec.displayName })}
                      onChange={(next) => {
                        setMarksMap((prev) => ({ ...prev, [rec.learnerId]: next }))
                      }}
                    />
                    <span
                      className={[
                        'classMarks__flag',
                        isLow ? 'classMarks__flag--low' : isAb ? '' : 'classMarks__flag--ok',
                      ].join(' ')}
                    >
                      {isAb ? 'AB' : isLow ? 'low' : ''}
                    </span>
                  </div>
                )
              })}
            </div>

            <div className="classHomework__bar">
              <Button
                variant="secondary"
                disabled
                title={t('NLSEduMobileDraftRemarksHint')}
              >
                {t('NLSEduMobileDraftRemarks')}
              </Button>
              <Button
                variant="primary"
                disabled={marksLocked}
                onClick={handleLockMarks}
              >
                {marksLocked ? t('NLSEduMobileMarksLocked') : t('NLSEduMobileLockMarks')}
              </Button>
            </div>
          </div>
        )
      }

      case 'learners': {
        const firstClassId = learnersList[0]?.currentNodeIds?.[0] ?? ''
        const learnerClassTitle = firstClassId ? (elementById.get(firstClassId) ?? '') : ''
        const learnersHeader = [
          learnerClassTitle,
          `${learnersList.length} ${isAcademy || isCorporate ? t('NLSEduMobileSubtabTrainees') : t('NLSEduMobileSubtabStudents')}`,
        ]
          .filter(Boolean)
          .join(' · ')

        return (
          <div className="classLearners">
            <div className="classTab__sectionHeader">
              <span>{learnersHeader}</span>
            </div>

            {learnersLoading ? <SectionLoader /> : null}

            {!learnersLoading && learnersList.length === 0 ? (
              <div className="classTab__emptyState" data-testid="learners-empty-state">
                {t('NLSEduMobileNoLearners')}
              </div>
            ) : null}

            <div className="classLearners__list">
              {learnersList.map((learner) => {
                const pct = attendancePcts[learner.id] ?? 100
                const lowAttendance = pct < 85
                return (
                  <div
                    key={learner.id}
                    className="classLearners__row"
                    onClick={() => setSelectedLearner(learner)}
                    role="button"
                    tabIndex={0}
                    aria-label={t('NLSEduMobileLearnerAttendanceAria', { name: learner.displayName })}
                  >
                    <div>
                      <div className="classToday__name">{learner.displayName}</div>
                      <div className="classToday__sub">
                        {lowAttendance ? t('NLSEduMobileLowAttendance') : t('NLSEduMobileAttendanceOnTrack')}
                      </div>
                    </div>
                    <div>
                      <div className="classLearners__pct">{`${pct}%`}</div>
                      <div className="classToday__sub">{t('NLSEduMobileAttendance')}</div>
                    </div>
                  </div>
                )
              })}
            </div>

            {selectedLearner ? (
              <div
                className="classTab__emptyState"
                data-testid="learner-summary-dialog"
              >
                <div>
                  <strong>{selectedLearner.displayName}</strong>
                </div>
                <div>{t('NLSEduMobileRollNumber', { rollNo: selectedLearner.admissionNo || selectedLearner.rollNo || '-' })}</div>
                <div>
                  {[
                    selectedLearner.currentNodeIds?.[0] ? elementById.get(selectedLearner.currentNodeIds[0]) : null,
                    t('NLSEduMobileEnrolled'),
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </div>
                <div style={{ marginTop: 8 }}>
                  <Button variant="secondary" onClick={() => setSelectedLearner(null)}>
                    {t('NLSEduMobileClose')}
                  </Button>
                </div>
              </div>
            ) : null}
          </div>
        )
      }

      // Admin / Teacher subtabs for EM6
      case 'fees':
        return <ClassFeesPanel workspaceId={workspace.id} />
      case 'admissions':
        return <ClassAdmissionsPanel workspaceId={workspace.id} elements={elements} />
      case 'timetable':
        return <ClassTimetablePanel workspaceId={workspace.id} />
      case 'feed':
        return (
          <ClassFeedPanel
            workspaceId={workspace.id}
            isTeacher={!isAdmin && isTeacherUser}
            classes={classes}
            feedKind={isAcademy || isCorporate ? 'TRAINING' : 'TEACHING'}
          />
        )
      default:
        return <div data-testid="admin-subtab-slot" />
    }
  }

  return (
    <div
      className={['classTab', !isMobile ? 'classTab--desktop' : ''].filter(Boolean).join(' ')}
      data-testid="class-tab-root"
    >
      <div className="classTab__appBar">
        <div className="classTab__wsHeader">
          <span>{workspace.name}</span>
          <span className="classTab__queuedIndicator">
            <CloudQueueRoundedIcon fontSize="inherit" /> 1 queued
          </span>
        </div>
        <h3 className="classTab__title">{tabTitle}</h3>
      </div>

      <div className="classTab__content">{renderContent()}</div>

      <MobileSubtabBar
        items={subtabItems}
        activeKey={activeSubtab}
        onChange={(key) => setActiveSubtab(key as ClassSubtabKey)}
        ariaLabel={tabTitle}
        testId="class-subtabs"
        variant="pills"
        compact
      />
    </div>
  )
}

export default ClassTab
